import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Alert, Col, Modal, Row } from 'react-bootstrap';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import { ITherapy } from '../../../util/types';
import LoadingSpinner from '../../../components/LoadingSpinner';
import SecondaryButton from '../../../components/SecondaryButton';
import EmptyState from '../../../components/EmptyState';
import AppModal from '../../../components/app-modal/AppModal';
import CreateAppointmentForm from '../../../components/appointment/CreateAppointmentForm';
import AppointmentList from '../../../components/appointment/AppointmentList';
import { AppointmentDto } from '../../../../../shared/dtos/appointment.dto';

type DurationMin = 15 | 30 | 45 | 60;

type CreatePayload = {
  start: string;
  end: string;
  duration: DurationMin;
};

type PatientAppointmentsProps = {
  therapy: ITherapy;
};

const PatientAppointments: FC<PatientAppointmentsProps> = ({ therapy }: PatientAppointmentsProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();

  const [scheduledAppointments, setScheduledAppointments] = useState<AppointmentDto[]>([]);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const patientId = therapy.patient?._id;

  useEffect(() => {
    if (!patientId) {
      setIsLoading(false);

      return;
    }

    const run = async (): Promise<void> => {
      try {
        const appointmentFromRemote: AppointmentDto[] = await authFetch(`/api/v1/appointment/patient/web/${patientId}`);

        setScheduledAppointments(Array.isArray(appointmentFromRemote) ? appointmentFromRemote : []);
      } catch (error) {
        console.error('Error occured while fetching appointments', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    void run();
  }, [patientId]);

  const createAppointment = async (payload: CreatePayload): Promise<void> => {
    const response: AppointmentDto = await authFetch('/api/v1/appointment/', {
      method: 'POST',
      body: {
        patient: patientId,
        start: payload.start,
        end: payload.end,
        duration: payload.duration,
      },
    });

    setScheduledAppointments((prev) => [response, ...prev]);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return <Alert variant="danger">{i18next.t('generic.error')}</Alert>;
  }

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <SecondaryButton onClick={() => setShowAppointmentModal(true)}>
          <i className="fas fa-plus me-2" aria-hidden="true" />
          {i18next.t('therapyDetails.appointments.button')}
        </SecondaryButton>
      </div>

      <AppModal show={showAppointmentModal} onHide={() => setShowAppointmentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{i18next.t('createAppointmentForm.modalTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateAppointmentForm
            therapy={therapy}
            onCreateAppointment={async (payload: CreatePayload) => {
              await createAppointment(payload);
              setShowAppointmentModal(false);
            }}
          />
        </Modal.Body>
      </AppModal>

      {scheduledAppointments.length === 0 && (
        <EmptyState icon="fas fa-calendar-alt" message={i18next.t('therapyDetails.appointments.empty')} />
      )}

      <Row>
        <Col md={12}>
          <div className="overflow-auto overflow-x-hidden" style={{ maxHeight: '40vh' }}>
            <AppointmentList appointments={scheduledAppointments} />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default PatientAppointments;
